import { convertToRaw } from "draft-js";
import { useEffect, useState } from "react";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import KnowledgeBase from "../models/KnowledgeBase";
import KnowledgeBaseService from "../services/KnowledgeBaseService";
import RoleUtils from "../utils/RoleUtils";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "./useUpdateUrl";
import { Routes } from "../types/Routes";

const useKnowledgeBaseList = () => {
  const [storedKnowledgeBaseCategory, setStoredKnowledgeBaseCategory] = useLocalStorage<AutoCompleteUIDTO>(
    STORAGE_KEYS.STORED_KNOWLEDGE_BASE_CATEGORY,
    EMPTY_AUTOCOMPLETE
  );
  const [storedKnowledgeBaseCode, setStoredKnowledgeBaseCode] = useLocalStorage<AutoCompleteUIDTO>(
    STORAGE_KEYS.STORED_KNOWLEDGE_BASE_CODE,
    EMPTY_AUTOCOMPLETE
  );

  const [storedAll, setStoredAll] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_KNOWLEDGE_BASE_SEARCH_ALL, EMPTY_AUTOCOMPLETE);
  const [knowledgeBaseCategories, setKnowledgeBaseCategories] = useState<AutoCompleteUIDTO[]>([]);
  const [knowledgeBaseCodes, setKnowledgeBaseCodes] = useState<AutoCompleteUIDTO[]>([]);
  const [searchCategory, setSearchCategory] = useState<AutoCompleteUIDTO>(storedKnowledgeBaseCategory);
  const [searchCode, setSearchCode] = useState<AutoCompleteUIDTO>(storedKnowledgeBaseCode);
  const [searchAll, setSearchAll] = useState<AutoCompleteUIDTO>(storedAll);
  const [clearCode, setClearCode] = useState<AutoCompleteUIDTO>();
  const [clearCategory, setClearCategory] = useState<AutoCompleteUIDTO>();
  const [authorizedRole, setAuthorizedRole] = useState<Boolean>(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | undefined>(undefined);
  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [searchByCategory, setSearchByCategory] = useState<Boolean>(false);
  const [searchByCode, setSearchByCode] = useState<Boolean>(false);
  const [searchByAll, setSearchByAll] = useState<Boolean>(false);
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [contentLink, setContentLink] = useState<string | undefined>(undefined);
  const [onEditContentLink, setOnEditContentLink] = useState<string | undefined>(undefined);

  const searchParams = new URLSearchParams(useLocation().search);
  const code = searchParams.get("code");
  const category = searchParams.get("category");
  const {updateUrl} = useUpdateUrl(Routes.KNOWLEDGE_BASE_LIST, searchParams);

  useEffect(() => {
    init();
    setAuthorizedRole(RoleUtils.hasSomeRoleExceptHR());

    if (category !== null) {
      const categoryFilter = knowledgeBaseCategories.find((knowledgeBaseCategory) => knowledgeBaseCategory.externalId === category);
      if (categoryFilter) setStoredKnowledgeBaseCategory(categoryFilter);
      setSearchByCategory(true);
      searchParams.delete("code");
    } else if (storedKnowledgeBaseCategory !== undefined && storedKnowledgeBaseCategory.externalId !== "") {
      searchParams.set("category", storedKnowledgeBaseCategory.externalId);
      setSearchByCategory(true);
    }

    if (code !== null) {
      const codeFilter = knowledgeBaseCodes.find((knowledgeBaseCode) => knowledgeBaseCode.externalId === code);
      if (codeFilter) setStoredKnowledgeBaseCode(codeFilter);
      setSearchByCode(true);
      searchParams.delete("category");
    } else if (storedKnowledgeBaseCode !== undefined && storedKnowledgeBaseCode.externalId !== "") {
      searchParams.set("code", storedKnowledgeBaseCode.externalId);
      setSearchByCode(true);
    }

    updateUrl();
  }, [update]);

  const init = async () => {
    try {
      const responses = await Promise.all([
        KnowledgeBaseService.findKnowledgeBaseCategories(),
        KnowledgeBaseService.findKnowledgeBaseCodes(),
      ]);

      const [responseCategories, responseCodes] = responses;

      setKnowledgeBaseCategories(responseCategories.getContent());
      setKnowledgeBaseCodes(responseCodes.getContent());
    } catch (error: any) {
      NotificationService.error(error || "Error getting knowledge data");
    }
  };

  const handleOnDeleteKnowledgeBase = async () => {
    if (!selectedKnowledgeBase) return;

    try {
      await KnowledgeBaseService.delete(selectedKnowledgeBase);

      setSelectedKnowledgeBase(undefined);
      setUpdate(Date.now());
    } catch (error) {
      NotificationService.error("Error trying to delete a Bad Smell");
    }
  };

  const setOnDataFetch = (pageNumber: number, count: number) => {
    if (searchByCategory === false && searchByCode === false) {
      return KnowledgeBaseService.getAll(pageNumber, count).then((result) => result.getContent());
    } else {
      if (searchByAll) {
        return KnowledgeBaseService.getAll(pageNumber, count).then((result) => result.getContent());
      } else {
        if (searchByCategory) {
          localStorage.removeItem("STORED_KNOWLEDGE_BASE_CODE");

          return KnowledgeBaseService.findByCategory(pageNumber, count, searchCategory?.label ? searchCategory.label : "").then((result) =>
            result.getContent()
          );
        } else {
          localStorage.removeItem("STORED_KNOWLEDGE_BASE_CATEGORY");

          return KnowledgeBaseService.findByCode(pageNumber, count, searchCode?.label ? searchCode.label : "").then((result) =>
            result.getContent()
          );
        }
      }
    }
  };

  const updateStoredKnowledgeBaseInStorage = (knowledgeBase: KnowledgeBase) => {
    setStoredKnowledgeBaseCode({ externalId: knowledgeBase.externalId || "", label: knowledgeBase.title });
  };

  const saveContentLink = (event: any) => {
    const rteContent = convertToRaw(event.getCurrentContent());
    rteContent && setContentLink(JSON.stringify(rteContent));
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const onCreate = authorizedRole
    ? (knowledgeBase: KnowledgeBase) => {
        knowledgeBase.contentLink = contentLink;
        return KnowledgeBaseService.create(knowledgeBase).then(() => setUpdate(Date.now()));
      }
    : undefined;

  const onUpdate = authorizedRole
    ? (knowledgeBase: KnowledgeBase) => {
        knowledgeBase.contentLink = contentLink;
        KnowledgeBaseService.update(knowledgeBase)
          .then((data) => {
            setOnEditContentLink(undefined);
            updateStoredKnowledgeBaseInStorage(data.getContent());
            setUpdate(Date.now());
          })
          .catch((error) => NotificationService.error("Error updating a Knowledge Base"));
      }
    : undefined;

  const onEdit = authorizedRole ? (knowledgeBase: KnowledgeBase) => setOnEditContentLink(knowledgeBase.contentLink) : undefined;

  const onCancel = authorizedRole
    ? () => {
        setOnEditContentLink(undefined);
        setContentLink(undefined);
      }
    : undefined;

  const hideActions = !authorizedRole;

  const onChangeCategory = (newValue: AutoCompleteUIDTO) => {
    setSearchCategory(newValue);
    setStoredKnowledgeBaseCategory(newValue);
    setSearchByCategory(true);
    setSearchByCode(false);
    setSearchCode(clearCode!);
    newValue === searchAll ? setSearchByAll(true) : setSearchByAll(false);
    searchParams.set("category", newValue.externalId);
    searchParams.delete("code");
    updateUrl();
  };

  const onChangeCode = (newValue: AutoCompleteUIDTO) => {
    setSearchCode(newValue);
    setStoredKnowledgeBaseCode(newValue);
    setSearchByCode(true);
    setSearchByCategory(false);
    setSearchCategory(clearCategory!);
    newValue === searchAll ? setSearchByAll(true) : setSearchByAll(false);
    searchParams.set("code", newValue.externalId);
    searchParams.delete("category");
    updateUrl();
  };

  return {
    searchAll,
    knowledgeBaseCategories,
    searchCategory,
    onChangeCategory,
    searchCode,
    onChangeCode,
    setOnDataFetch,
    update,
    onCreate,
    onUpdate,
    onEdit,
    onCancel,
    hideActions,
    onEditContentLink,
    setOpenDetails,
    setSelectedKnowledgeBase,
    setOpenDelete,
    openDelete,
    selectedKnowledgeBase,
    handleOnDeleteKnowledgeBase,
    handleCloseDetails,
    knowledgeBaseCodes,
    openDetails,
    saveContentLink,
    authorizedRole,
  };
};

export default useKnowledgeBaseList;
